/**
 * AddressInput
 *
 * A self-contained Google Places Autocomplete input component.
 * Owns all of its own local state: the typed value, the dropdown suggestions,
 * and the "active" (focused) flag. The parent receives exactly ONE clean
 * callback — onChange(formattedString) — only after the Places API has
 * resolved the full address including zip code.
 *
 * This isolation is the key design decision: it prevents any race condition
 * between the async fetchPlaceDetails call and React re-renders in the parent.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLoadScript } from '@react-google-maps/api';

const libraries: any = ['places'];

// ---------------------------------------------------------------------------
// Hook: useNewPlacesAutocomplete
// ---------------------------------------------------------------------------
function useNewPlacesAutocomplete(selectedCountry: string) {
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const sessionTokenRef = useRef<any>(null);
    const requestIdRef = useRef(0);

    const initToken = () => {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    };

    const fetchSuggestions = useCallback(async (input: string) => {
        const reqId = ++requestIdRef.current;
        if (!input) {
            setSuggestions([]);
            return;
        }
        if (!window.google?.maps?.places?.AutocompleteSuggestion) return;
        initToken();

        try {
            const request: any = { input, sessionToken: sessionTokenRef.current };
            if (selectedCountry) request.includedRegionCodes = [selectedCountry.toLowerCase()];
            const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            if (reqId === requestIdRef.current) {
                setSuggestions(response.suggestions || []);
            }
        } catch {
            if (reqId === requestIdRef.current) {
                setSuggestions([]);
            }
        }
    }, [selectedCountry]);

    const fetchPlaceDetails = async (suggestion: any): Promise<any> => {
        const placeId = suggestion?.placePrediction?.placeId || suggestion?.place_id;
        try {
            if (typeof suggestion?.placePrediction?.toPlace === 'function') {
                const place = suggestion.placePrediction.toPlace();
                await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
                sessionTokenRef.current = null;
                return place;
            }
            if (placeId && window.google?.maps?.places?.Place) {
                const place = new window.google.maps.places.Place({ id: placeId });
                await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
                sessionTokenRef.current = null;
                return place;
            }
        } catch {
            // Fall through to legacy PlacesService if Place.fetchFields fails
        }
        if (!placeId || !window.google?.maps?.places?.PlacesService) {
            throw new Error('PlacesService unavailable');
        }
        return new Promise((resolve, reject) => {
            const div = document.createElement('div');
            div.style.display = 'none';
            document.body.appendChild(div);
            const service = new window.google.maps.places.PlacesService(div);
            service.getDetails({ placeId, fields: ['address_components', 'formatted_address'] }, (place: any, status: any) => {
                document.body.removeChild(div);
                sessionTokenRef.current = null;
                if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                    resolve(place);
                } else {
                    reject(new Error(String(status)));
                }
            });
        });
    };

    const clearSuggestions = useCallback(() => {
        requestIdRef.current++;
        setSuggestions([]);
    }, []);

    return { suggestions, fetchSuggestions, clearSuggestions, fetchPlaceDetails };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface AddressInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
}

const AddressInputInner: React.FC<AddressInputProps> = ({ value, onChange, placeholder = 'Search address...', className }) => {
    const { fetchSuggestions, suggestions, clearSuggestions, fetchPlaceDetails } = useNewPlacesAutocomplete('US');
    const [isActive, setIsActive] = useState(false);
    const [localValue, setLocalValue] = useState(value || '');

    // Keep local display value in sync when parent value changes externally
    useEffect(() => {
        if (!isActive) setLocalValue(value || '');
    }, [value, isActive]);

    const handleSelect = async (suggestion: any) => {
        const predictionText = (suggestion?.placePrediction?.text?.text || suggestion?.description || '')
            .replace(/,\s*USA$/, '')
            .replace(/\s*USA$/, '')
            .trim();

        // Immediately close the dropdown and commit the selected prediction text synchronously
        // so the field never reverts to just the typed street number while awaiting place details.
        clearSuggestions();
        setIsActive(false);
        if (predictionText) {
            setLocalValue(predictionText);
            onChange(predictionText);
        }

        try {
            const place = await fetchPlaceDetails(suggestion);

            // Build the formatted string (new API uses camelCase, old uses snake_case)
            const apiFormatted = (place?.formatted_address || place?.formattedAddress || '')
                .replace(/,\s*USA$/, '')
                .replace(/\s*USA$/, '')
                .trim();

            // Prefer apiFormatted only when it is a complete address; never downgrade predictionText to a shorter fragment
            let formatted = (apiFormatted && apiFormatted.includes(',') && apiFormatted.length >= predictionText.length)
                ? apiFormatted
                : (predictionText || apiFormatted);

            // Extract zip code (new API uses longText, old uses long_name)
            let zip = '';
            const comps = place?.address_components || place?.addressComponents;
            comps?.forEach((c: any) => {
                const types = c.types || [];
                if (types.includes('postal_code')) zip = c.long_name || c.longText || '';
            });

            // Append zip if missing from the formatted string
            if (zip && !formatted.includes(zip)) {
                formatted = `${formatted} ${zip}`;
            }

            if (formatted) {
                setLocalValue(formatted);
                onChange(formatted);
            }
        } catch {
            // Fallback: ensure the prediction text remains set
            if (predictionText) {
                setLocalValue(predictionText);
                onChange(predictionText);
            }
        }
    };

    const defaultClass = 'w-full px-4 py-2 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6]';

    return (
        <div className="relative">
            <input
                type="text"
                className={className ?? defaultClass}
                value={isActive ? localValue : (value || '')}
                placeholder={placeholder}
                onChange={e => {
                    setIsActive(true);
                    setLocalValue(e.target.value);
                    onChange(e.target.value);
                    fetchSuggestions(e.target.value);
                }}
                onFocus={() => {
                    setIsActive(true);
                    setLocalValue(value || '');
                }}
                onBlur={() => setTimeout(() => { setIsActive(false); clearSuggestions(); }, 200)}
            />
            {isActive && suggestions.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border border-[#D7DFEC] rounded shadow-lg mt-1 max-h-60 overflow-auto">
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-[14px] text-[#141435]"
                            onMouseDown={e => {
                                e.preventDefault();
                                handleSelect(s);
                            }}
                        >
                            {s.placePrediction.text.text}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// Wrapper that waits for the Google Maps script to load
const AddressInput: React.FC<AddressInputProps> = (props) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
        libraries,
        version: 'weekly',
    });

    if (!isLoaded) {
        return (
            <input
                type="text"
                className={props.className ?? 'w-full px-4 py-2 rounded-lg border border-[#D7DFEC] font-plus-jakarta focus:outline-none focus:ring-2 focus:ring-[#0B4BB6]'}
                value={props.value || ''}
                onChange={e => props.onChange(e.target.value)}
                placeholder={props.placeholder ?? 'Search address...'}
            />
        );
    }

    return <AddressInputInner {...props} />;
};

export default AddressInput;
